import { type JSX } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import { type CompetitivePositionItemData } from '@/features/benchmarking/components/CompetitivePositionCard/CompetitivePositionCard.types';
import { type MediaCard } from '@/features/benchmarking/schemas/benchmarkingHeader';
import { type EscolaInfoResponse } from '@/features/benchmarking/schemas/benchmarkingInfo';

import { PdfDataTable, type PdfDataTableRow } from '../components/PdfDataTable';
import { ReportPage } from '../components/ReportPage';
import { arrowAssets } from '../lib/arrowAssets';
import { PDF_FONT_WEIGHTS, PDF_FONTS, PDF_PAGE, PDF_SPACING, PDF_TEXT_STYLES } from '../lib/pdfTokens';

// ---------------------------------------------------------------------------
// Resolved token values used across sections
// ---------------------------------------------------------------------------

const TEXT_PRIMARY = '#25252A';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  // Title block
  titleBlock: { marginBottom: PDF_SPACING[2] },
  title: { ...PDF_TEXT_STYLES.pageTitle, marginBottom: 4 },
  subtitle: PDF_TEXT_STYLES.pageSubtitle,

  // Two-column intro
  introRow: { flexDirection: 'row', gap: PDF_SPACING[6], marginBottom: PDF_SPACING[2] },
  bodyText: { ...PDF_TEXT_STYLES.body, flex: 1 },
  metaColumn: { width: 258 },

  // Stat cards row
  statCardsRow: {
    flexDirection: 'row',
    gap: PDF_SPACING[2],
    marginBottom: PDF_SPACING[4],
    marginHorizontal: -PDF_PAGE.cardBleed,
  },
  statCard: {
    flex: 1,
    borderRadius: 8,
    padding: 8,
    gap: 2,
  },
  statLabel: {
    fontFamily: PDF_FONTS.sans,
    fontWeight: 500,
    fontSize: 9,
    lineHeight: 1.35,
  },
  statValue: {
    fontFamily: PDF_FONTS.sans,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontSize: 14,
    lineHeight: 1.35,
  },

  // Ranking rows
  rankingList: { gap: 8, marginBottom: PDF_SPACING[4], marginHorizontal: -PDF_PAGE.cardBleed },
  rankingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PDF_SPACING[2],
    height: 66,
    borderRadius: 12,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 2,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D9D9E3',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: PDF_PAGE.cardBleed,
  },
  rankingRowCurrentRing: {
    borderRadius: 14,
    borderWidth: 1.5,
    borderStyle: 'solid',
    borderColor: '#007A5C',
    padding: 3,
    marginHorizontal: -3,
  },
  rankBadge: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  rankBadgeText: { fontSize: 12, fontWeight: PDF_FONT_WEIGHTS.semiBold, fontFamily: PDF_FONTS.sans, lineHeight: 1.35 },
  rankSchool: { flex: 1 },
  rankSchoolName: {
    fontSize: 12,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    marginBottom: 8,
  },
  rankSchoolMeta: { fontSize: 10, fontWeight: 300, fontFamily: PDF_FONTS.sans, color: TEXT_PRIMARY },
  rankScoreCol: { width: 90, flexShrink: 0 },
  rankScoreLabel: {
    fontSize: 8,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: 0.32,
    lineHeight: 1.35,
    marginBottom: 2,
  },
  rankScoreValue: { fontSize: 12, fontWeight: 300, fontFamily: PDF_FONTS.sans, color: TEXT_PRIMARY, lineHeight: 1.35 },
  // Fits the longest label ("999 posições à frente" + arrow) on a single line.
  rankBadgeTagCol: { width: 112, flexShrink: 0, alignItems: 'flex-start' },
  rankBadgeTag: {
    borderRadius: 40,
    borderWidth: 1,
    borderStyle: 'solid',
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rankBadgeTagText: {
    fontSize: 8.5,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    lineHeight: 1.35,
  },
});

// ---------------------------------------------------------------------------
// Tone colour helpers
// ---------------------------------------------------------------------------

type Tone = CompetitivePositionItemData['tone'];

type ToneColors = { bg: string; border: string; color: string };

function toneColors(tone: Tone): ToneColors {
  if (tone === 'current') return { bg: '#C3F2DC', border: '#007A5C', color: '#00513D' };
  if (tone === 'behind') return { bg: '#FBEFC5', border: '#956D00', color: '#614700' };
  return { bg: '#D6F1FF', border: '#006A9F', color: '#004D73' };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type EscolaInfoSubset = Pick<
  EscolaInfoResponse,
  'nomeEscola' | 'perfil' | 'cidade' | 'uf' | 'qtdAlunos' | 'qtdTurmas' | 'mediaRed1000' | 'mediaEnem' | 'metaEscola'
>;

function StatCard({
  label,
  value,
  bg,
  labelColor,
  valueColor,
}: {
  label: string;
  value: number | null;
  bg: string;
  labelColor: string;
  valueColor: string;
}): JSX.Element {
  return (
    <View style={[styles.statCard, { backgroundColor: bg }]}>
      <Text style={[styles.statLabel, { color: labelColor }]}>{label}</Text>
      <Text style={[styles.statValue, { color: valueColor }]}>{value ?? '—'}</Text>
    </View>
  );
}

function RankingRow({ item }: { item: CompetitivePositionItemData }): JSX.Element {
  const { tone } = item;
  const isCurrent = tone === 'current';
  const colors = toneColors(tone);
  const meta = [item.studentsCount !== null ? `${item.studentsCount} alunos` : null, item.city]
    .filter(Boolean)
    .join('  ·  ');

  const inner = (
    <View
      style={styles.rankingRow}
      wrap={false}>
      <View style={[styles.rankBadge, { backgroundColor: colors.bg }]}>
        <Text style={[styles.rankBadgeText, { color: colors.color }]}>{item.position}º</Text>
      </View>
      <View style={styles.rankSchool}>
        <Text style={styles.rankSchoolName}>{item.schoolName}</Text>
        {meta ? <Text style={styles.rankSchoolMeta}>{meta}</Text> : null}
      </View>
      <View style={styles.rankScoreCol}>
        <Text style={styles.rankScoreLabel}>{item.currentLabel}</Text>
        <Text style={styles.rankScoreValue}>{item.currentScore} pts</Text>
      </View>
      <View style={styles.rankScoreCol}>
        <Text style={styles.rankScoreLabel}>{item.projectedLabel}</Text>
        <Text style={styles.rankScoreValue}>{item.projectedScore}</Text>
      </View>
      <View style={styles.rankBadgeTagCol}>
        {tone ? (
          <View style={[styles.rankBadgeTag, { backgroundColor: colors.bg, borderColor: colors.border }]}>
            {tone !== 'current' && (
              <Image
                src={tone === 'ahead' ? arrowAssets.ahead : arrowAssets.behind}
                style={{ width: 6, height: 7 }}
              />
            )}
            <Text style={[styles.rankBadgeTagText, { color: colors.color }]}>{item.advantageLabel}</Text>
          </View>
        ) : null}
      </View>
    </View>
  );

  if (isCurrent) {
    return <View style={styles.rankingRowCurrentRing}>{inner}</View>;
  }

  return inner;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type CompetitivePageProps = {
  escolaInfo: EscolaInfoSubset;
  rankingItems?: CompetitivePositionItemData[];
  benchmarkAverages?: MediaCard[];
};

export function CompetitivePage({
  escolaInfo,
  rankingItems = [],
  benchmarkAverages = [],
}: CompetitivePageProps): JSX.Element {
  const localizacao = [escolaInfo.cidade, escolaInfo.uf].filter(Boolean).join(', ');
  const alunosETurmas = [
    escolaInfo.qtdAlunos !== null ? `${escolaInfo.qtdAlunos} alunos` : null,
    escolaInfo.qtdTurmas !== null ? `${escolaInfo.qtdTurmas} turmas` : null,
  ]
    .filter(Boolean)
    .join(' | ');

  return (
    <ReportPage>
      {/* Title block */}
      <View style={styles.titleBlock}>
        <Text style={styles.title}>Posicionamento competitivo da sua escola</Text>
        <Text style={styles.subtitle}>
          Como sua escola se posiciona entre instituições de perfil semelhante no ENEM {new Date().getFullYear()}
        </Text>
      </View>

      {/* Two-column intro */}
      <View style={styles.introRow}>
        <View style={styles.bodyText}>
          <Text style={{ marginBottom: PDF_SPACING[2] }}>
            Esta análise compara o desempenho da sua escola com instituições da mesma região e potencialmente
            concorrentes, utilizando microdados oficiais do ENEM.
          </Text>
          <Text>
            Mais do que indicar uma posição no ranking, o recorte evidencia distâncias reais em pontos, permitindo
            identificar o quão próxima sua escola está de ultrapassar concorrentes diretos ou de ser alcançada por
            escolas com desempenho inferior.
          </Text>
        </View>
        <View style={styles.metaColumn}>
          <PdfDataTable
            rows={[
              escolaInfo.nomeEscola ? { label: 'Escola', value: escolaInfo.nomeEscola, singleLine: true } : null,
              escolaInfo.perfil ? { label: 'Perfil', value: escolaInfo.perfil } : null,
              localizacao ? { label: 'Localização', value: localizacao } : null,
              alunosETurmas ? { label: 'Alunos e Turmas', value: alunosETurmas } : null,
            ].filter((r): r is PdfDataTableRow => r !== null)}
          />
          <View style={[styles.statCardsRow, { marginTop: PDF_SPACING[3], marginHorizontal: 0 }]}>
            <StatCard
              label={'Média na\nRed 1000'}
              value={escolaInfo.mediaRed1000 ?? null}
              bg='#C3F2DC'
              labelColor='#053C37'
              valueColor='#07514B'
            />
            <StatCard
              label='Média no ENEM'
              value={escolaInfo.mediaEnem ?? null}
              bg='#C2EBFF'
              labelColor='#002F46'
              valueColor='#004D73'
            />
            <StatCard
              label='Meta da sua escola'
              value={escolaInfo.metaEscola ?? null}
              bg='#ECE0FF'
              labelColor='#36146A'
              valueColor='#4E16A7'
            />
          </View>
        </View>
      </View>

      {/* Ranking list */}
      <View style={styles.rankingList}>
        {rankingItems.map((item) => (
          <RankingRow
            key={item.id}
            item={item}
          />
        ))}
      </View>

      {/* Benchmark averages table */}
      <PdfDataTable
        rows={benchmarkAverages.map((avg) => ({
          label: avg.titulo,
          value: avg.pontuacao !== null ? `${avg.pontuacao} pts` : '—',
          extra:
            avg.qtdEscola !== null && avg.qtdEscola !== undefined
              ? `${avg.qtdEscola.toLocaleString('pt-BR')} escolas`
              : undefined,
        }))}
        labelWidth={220}
        extraWidth={100}
      />
    </ReportPage>
  );
}
